/*
 * Croatian Jewellery — pendant configurator
 * Rewritten 2026-10-12.
 * Metal-compatibility filtering added 2026-10-14.
 *
 * Changes from the previous version:
 *  1. Base price is read from a Liquid-rendered variant map, not scraped out of
 *     ".price" text. The old DOM scrape could pick up an unrelated price node and
 *     is the reason the subtotal could disagree with what the cart charged.
 *  2. Chain prices come from data-price-cents, rendered live from the product, so
 *     a displayed "+$X" is always the amount Shopify will charge.
 *  3. Metal compatibility: the handmade upgrade offers ONLY chains whose gold
 *     matches the selected pendant variant. A 9ct yellow gold pendant sees 9ct
 *     yellow gold chains, an 18ct yellow gold pendant sees 18ct yellow gold
 *     chains, and a carat with no matching chain in stock gets the made-to-order
 *     enquiry note instead. White gold, rose gold and silver are never offered a
 *     yellow gold chain. Every label names the actual carat on screen, so the
 *     panel can never describe an 18ct chain as 9ct. Nothing is preselected.
 *  4. Pendant and chain go to Shopify as ONE cart/add request carrying both
 *     line items, intercepted in the capture phase so the theme's own product
 *     form handler cannot also add the pendant. Covers Add to cart, the sticky
 *     bar and Buy now.
 */
(function () {
  'use strict';

  function money(cents) {
    return '$' + (cents / 100).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /* Read a carat and a gold colour out of a free-text metal string, so the same
     rules apply to a pendant option value ("9ct Yellow Gold", "Gold ct: 18ct")
     and to a chain's own metal label. Carat is returned as '9' | '14' | '18' | ''
     and an empty carat means "stated colour but unknown purity", which is never
     treated as a match. 18 and 14 are tested before 9 so "18ct" cannot be read
     as a 9. Millesimal marks (375 / 585 / 750) are accepted because the 18ct
     listing uses them. */
  function parseMetal(str) {
    var s = String(str || '').toLowerCase();

    var colour = 'other';
    if (s.indexOf('white gold') !== -1) colour = 'white';
    else if (s.indexOf('rose gold') !== -1 || s.indexOf('pink gold') !== -1) colour = 'rose';
    else if (s.indexOf('yellow gold') !== -1) colour = 'yellow';
    else if (s.indexOf('sterling') !== -1 || s.indexOf('silver') !== -1) colour = 'silver';

    var carat = '';
    if (/18\s*(?:ct|k|kt|carat|karat)/.test(s) || s.indexOf('750') !== -1) carat = '18';
    else if (/14\s*(?:ct|k|kt|carat|karat)/.test(s) || s.indexOf('585') !== -1) carat = '14';
    else if (/9\s*(?:ct|k|kt|carat|karat)/.test(s) || s.indexOf('375') !== -1) carat = '9';

    return { carat: carat, colour: colour };
  }

  function caratWord(c) {
    return c ? c + 'ct' : '';
  }

  function init() {
    var root = document.querySelector('cj-chain-configurator');
    if (!root) return;

    var form = productForm();
    var subtotalEl = document.getElementById('cjSubtotalAmount');

    var variants = {};
    var mapEl = root.querySelector('[data-cj-variant-metals]');
    if (mapEl) {
      try { variants = JSON.parse(mapEl.textContent) || {}; }
      catch (e) { variants = {}; }
    }

    var toggleBtns = Array.prototype.slice.call(root.querySelectorAll('.cj-chain-opt'));
    var hmBtn = root.querySelector('.cj-chain-opt--hm');
    var hmBtnCarat = root.querySelector('[data-cj-hm-carat]');
    var stdPanel = root.querySelector('.cj-chain-options');
    var hmPanel = root.querySelector('.cj-hm-panel');
    var hmCards = Array.prototype.slice.call(root.querySelectorAll('.cj-hm-card'));
    var hmNote = root.querySelector('[data-cj-hm-note]');
    var hmIntro = root.querySelector('[data-cj-hm-intro]');
    var enquiry = root.querySelector('.cj-hm-enquiry');
    var enqTitle = root.querySelector('[data-cj-enq-title]');
    var enqBody = root.querySelector('[data-cj-enq-body]');

    var chainMode = 'none';

    function currentVariantId() {
      var f0 = productForm();
      if (!f0) return null;
      var f = f0.querySelector('[name="id"]');
      return f && f.value ? String(f.value) : null;
    }

    function currentMetal() {
      var id = currentVariantId();
      var rec = id && variants[id];
      return rec && rec.m ? String(rec.m) : '';
    }

    function basePriceCents() {
      var id = currentVariantId();
      var rec = id && variants[id];
      if (rec && typeof rec.p === 'number') return rec.p;
      var dp = parseFloat(root.getAttribute('data-base-price'));
      return isNaN(dp) ? 0 : Math.round(dp * 100);
    }

    /* The pendant's own name, for the chain line item's "Added with" property.
       document.title carries the store-name suffix, so prefer the page heading. */
    function pendantName() {
      var h = document.querySelector('.product-title, .product__title, h1');
      var t = h && h.textContent ? h.textContent.trim().replace(/\s+/g, ' ') : '';
      return t || document.title;
    }

    /* Inline, non-modal error. window.alert blocks the page and is suppressed in
       some embedded contexts, so failures must be surfaced in the document. */
    function showError(msg) {
      var box = root.querySelector('.cj-chain-error');
      if (!box) {
        box = document.createElement('p');
        box.className = 'cj-chain-error';
        box.setAttribute('role', 'alert');
        root.appendChild(box);
      }
      box.textContent = msg;
      box.scrollIntoView({ block: 'nearest' });
    }

    /* A radio inside a hidden card must never count as the customer's choice:
       the card is hidden precisely because that chain does not match the metal
       now selected, and returning it here would let it reach the cart. */
    function selectedChain() {
      var rs = root.querySelectorAll('.cj-chain-radio:checked');
      for (var i = 0; i < rs.length; i++) {
        var card = rs[i].closest('.cj-hm-card, .cj-chain-option-card');
        if (card && card.hidden) continue;
        return rs[i];
      }
      return null;
    }

    function selectedChainCents() {
      var r = selectedChain();
      if (!r) return 0;
      if (r.dataset.priceCents) return parseInt(r.dataset.priceCents, 10) || 0;
      var v = parseFloat(r.dataset.price);
      return isNaN(v) ? 0 : Math.round(v * 100);
    }

    function clearChain() {
      root.querySelectorAll('.cj-chain-radio').forEach(function (r) { r.checked = false; });
    }

    function updateSubtotal() {
      if (!subtotalEl) return;
      subtotalEl.textContent = money(basePriceCents() + selectedChainCents());
    }

    function setMode(mode) {
      chainMode = mode;
      toggleBtns.forEach(function (b) {
        b.classList.toggle('cj-selected', b.dataset.chainMode === mode);
      });
      if (stdPanel) stdPanel.hidden = mode !== 'add';
      if (hmPanel) hmPanel.hidden = mode !== 'handmade';
      if (mode !== 'add' && mode !== 'handmade') clearChain();
      updateSubtotal();
    }

    /* Metal compatibility -------------------------------------------------
       Runs on load and on every variant change. The handmade panel is rendered
       with a card for every available handmade chain; this is what decides which
       of those cards the customer may actually see and choose.

         pendant 9ct  yellow gold -> 9ct  yellow gold chains only
         pendant 18ct yellow gold -> 18ct yellow gold chains only
         pendant 14ct yellow gold -> no 14ct chain is stocked, so the
                                     made-to-order enquiry note is shown
         white gold / rose gold / silver / unknown purity
                                  -> no handmade chain offered at all

       A card that is hidden also has its radio cleared, so a chain chosen under
       one metal cannot survive a switch to another and reach the cart. */
    function applyMetal() {
      var pm = parseMetal(currentMetal());
      var isYellow = pm.colour === 'yellow';
      var matching = 0;

      hmCards.forEach(function (card) {
        var ok = isYellow && !!pm.carat &&
          card.dataset.carat === pm.carat &&
          card.dataset.colour === 'yellow';
        card.hidden = !ok;
        if (ok) {
          matching++;
        } else {
          var r = card.querySelector('.cj-chain-radio');
          if (r && r.checked) r.checked = false;
        }
      });

      var compatible = matching > 0;
      var enquirable = isYellow && !!pm.carat && !compatible;
      var word = caratWord(pm.carat);

      if (hmBtn) hmBtn.hidden = !compatible;
      if (hmBtnCarat) hmBtnCarat.textContent = compatible ? 'Solid ' + word : '';
      if (enquiry) enquiry.hidden = !enquirable;

      if (compatible) {
        if (hmIntro) {
          hmIntro.textContent = 'Complete your pendant with a handmade chain in solid ' + word +
            ' yellow gold, matched to the gold of this piece.';
        }
        if (hmNote) {
          hmNote.textContent = 'Every chain shown here is solid ' + word +
            ' yellow gold, the same gold as the pendant you have selected, and is added to your order as its own item.';
        }
      }

      if (enquirable) {
        if (enqTitle) enqTitle.textContent = 'Handmade chains in ' + word + ' yellow gold';
        if (enqBody) {
          enqBody.textContent = 'We do not currently hold a handmade chain in solid ' + word +
            ' yellow gold. Any of our handmade chains can be made to order in solid ' + word +
            ' yellow gold to match this pendant.';
        }
      }

      if (!compatible && chainMode === 'handmade') {
        clearChain();
        setMode('none');
      } else {
        updateSubtotal();
      }
    }

    toggleBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        clearChain();
        setMode(btn.dataset.chainMode);
      });
    });

    root.querySelectorAll('.cj-chain-radio').forEach(function (radio) {
      radio.addEventListener('change', updateSubtotal);
    });

    /* React to variant changes without polling the rendered price ---------- */
    if (form) {
      form.addEventListener('change', function () {
        setTimeout(applyMetal, 0);
      });
      var idInput = form.querySelector('[name="id"]');
      if (idInput && 'MutationObserver' in window) {
        new MutationObserver(applyMetal).observe(idInput, {
          attributes: true,
          attributeFilter: ['value']
        });
      }
    }
    document.addEventListener('variant:change', applyMetal);
    document.addEventListener('variantChange', applyMetal);

    var lastId = currentVariantId();
    setInterval(function () {
      var id = currentVariantId();
      if (id !== lastId) { lastId = id; applyMetal(); }
    }, 500);

    applyMetal();
    setMode('none');

    /* Add to cart ----------------------------------------------------------
       Rewritten 2026-10-13 after a customer-facing failure found in manual
       testing: the cart received the pendant twice and no chain at all, and
       Buy now reached checkout with the pendant only.

       THREE controls can start an add on a product page, and the configurator
       has to own all of them once a chain has been chosen:

         1. the in-form "Add to cart" submit button  -> fires the form's submit
         2. the sticky bar button                    -> puppets button 1
         3. "Buy now" (cj-buy-now)                   -> its own click handler,
                                                        posts the form itself and
                                                        goes straight to checkout

       Only (1) used to be handled, by a SECOND submit listener on the form.
       e.preventDefault() stops the browser's own navigation but it does not stop
       the theme's <product-form-component on:submit="/handleSubmit"> listener,
       which had already been invoked for the same event. Both handlers therefore
       posted their own add: the pendant went in twice, and because the chain was
       a separate follow-up request it could be cancelled by whichever handler
       navigated first. (3) never involved the configurator at all.

       Now a capture-phase listener on document runs before any theme listener and
       stops the event there, so exactly ONE request is sent and it carries BOTH
       line items. Binding on document rather than on the form node also survives
       the section re-render that replaces the form when a variant changes. */

    var busy = false;

    function productForm() {
      return (
        document.querySelector('form[data-type="add-to-cart-form"]') ||
        document.querySelector('form[action*="/cart/add"]')
      );
    }

    function routeRoot() {
      return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }

    /* Only take over when the customer has actually chosen a chain. With no
       chain selected the theme's own add-to-cart is left completely untouched. */
    function shouldIntercept() {
      if (chainMode !== 'add' && chainMode !== 'handmade') return false;
      return !!selectedChain();
    }

    function chainPending() {
      var chain = selectedChain();
      return !!(chain && chain.dataset.pricePending === 'true');
    }

    /* Unreachable in normal use: an option awaiting price approval renders with a
       disabled radio, so it cannot be selected. Kept as a last line of defence.
       Deliberately not window.alert, which blocks the page. */
    function refusePending() {
      var chain = selectedChain();
      if (!chain) return;
      chain.checked = false;
      var card = chain.closest('.cj-chain-option-card');
      if (card) {
        var warn = card.querySelector('.cj-chain-unavail');
        if (warn) {
          warn.textContent = 'Not yet available to order';
          warn.setAttribute('role', 'alert');
        }
        card.scrollIntoView({ block: 'nearest' });
      }
      setMode('none');
    }

    /* Every add control is locked while a request is in flight, so a second tap
       on a slow connection cannot produce a second line. */
    function setBusy(state) {
      busy = state;
      var btns = document.querySelectorAll(
        'form[data-type="add-to-cart-form"] [type="submit"], .cj-buy-now__button, .sticky-add-to-cart__button'
      );
      Array.prototype.forEach.call(btns, function (b) {
        if (state) {
          b.setAttribute('aria-busy', 'true');
          b.disabled = true;
        } else {
          b.removeAttribute('aria-busy');
          b.disabled = false;
        }
      });
    }

    /* Both line items in ONE request. Shopify applies them together, so the
       pendant can never land without its chain and there is no second request
       left in flight for a navigation to cancel. */
    function buildItems(form) {
      var chain = selectedChain();
      var fd = new FormData(form);

      var qty = parseInt(fd.get('quantity') || '1', 10);
      if (!qty || qty < 1) qty = 1;

      var pendant = { id: fd.get('id'), quantity: qty };

      var props = {};
      var hasProps = false;
      fd.forEach(function (value, key) {
        var m = key.match(/^properties\[(.+)\]$/);
        if (m && String(value).length) {
          props[m[1]] = value;
          hasProps = true;
        }
      });
      if (hasProps) pendant.properties = props;
      if (fd.get('selling_plan')) pendant.selling_plan = fd.get('selling_plan');

      /* One chain per pendant, so two pendants arrive as two complete pieces. */
      return [
        pendant,
        {
          id: parseInt(chain.value, 10),
          quantity: qty,
          properties: { 'Added with': pendantName() }
        }
      ];
    }

    function addPendantAndChain(form, destination) {
      if (busy) return;
      if (chainPending()) {
        refusePending();
        return;
      }
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

      setBusy(true);

      fetch(routeRoot() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: buildItems(form) })
      })
        .then(function (r) {
          return r
            .json()
            .catch(function () { return null; })
            .then(function (d) {
              if (!r.ok) throw new Error((d && (d.description || d.message)) || 'Add to cart failed');
              return d;
            });
        })
        .then(function () {
          window.location.href = routeRoot() + destination;
        })
        .catch(function (err) {
          console.error('Configurator cart error:', err);
          setBusy(false);
          showError('Sorry, we could not add that to your cart. Please try again.');
        });
    }

    /* INTERCEPTION -----------------------------------------------------------
       Horizon registers its own listeners before this file runs, so stopping the
       SUBMIT event is not enough: its handler can already be queued on the same
       node. The click is cancelled instead. Cancelling the click also cancels the
       implicit form submission, so product-form-component.handleSubmit never runs
       and cannot add the pendant a second time, whatever the listener order.

         .cj-buy-now__button        -> its own click handler, goes to checkout
         .sticky-add-to-cart__button -> puppets the in-form button
         in-form [type="submit"]     -> the normal Add to cart

       The submit listener below stays as a net for the keyboard path (Enter in a
       form field), which produces a submit without a click. */
    function interceptDestination(target) {
      if (!target || !target.closest) return null;
      if (target.closest('.cj-buy-now__button')) return 'checkout';
      if (target.closest('.sticky-add-to-cart__button')) return 'cart';
      if (target.closest('form[data-type="add-to-cart-form"] [type="submit"]')) return 'cart';
      if (target.closest('add-to-cart-component button')) return 'cart';
      return null;
    }

    document.addEventListener(
      'click',
      function (e) {
        var dest = interceptDestination(e.target);
        if (!dest) return;
        if (!shouldIntercept()) return;

        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();

        var form = productForm();
        if (form) addPendantAndChain(form, dest);
      },
      true
    );

    document.addEventListener(
      'submit',
      function (e) {
        var form = e.target;
        if (!form || form.nodeName !== 'FORM') return;
        if (
          form.getAttribute('data-type') !== 'add-to-cart-form' &&
          (form.getAttribute('action') || '').indexOf('/cart/add') === -1
        ) {
          return;
        }
        if (!shouldIntercept()) return;

        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        addPendantAndChain(form, 'cart');
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
